import type { ReactNode } from 'react';
import type { Track } from '@blackblack/shared';
import { formatDuration } from '@blackblack/shared';
import { useTrack } from '../../lib/entities';
import { navigate } from '../../lib/router';
import { player, usePlayer } from '../../state/player';
import { Icon } from '../../components/ui/Icon';
import { IconButton, cx } from '../../components/ui/primitives';
import { ActionList, Sheet, useOpenSheet } from '../../components/ui/Sheet';
import { Cover } from './Cover';
import { useTrackActions } from './useTrackActions';
import styles from './music.module.css';

interface Props {
  track: Track;
  /** The list this row belongs to — tapping plays the list from this track. */
  list?: Track[];
  index?: number;
  context?: string;
  showCover?: boolean;
  number?: number;
  trailing?: ReactNode;
  extraActions?: Array<{ label: string; icon: ReactNode; onClick: () => void; danger?: boolean }>;
}

export function TrackRow({ track: base, list, index = 0, context, showCover = true, number, trailing, extraActions = [] }: Props) {
  const track = useTrack(base);
  const isCurrent = usePlayer((s) => s.queue[s.index]?.id === track.id);
  const playing = usePlayer((s) => s.playing);
  const actions = useTrackActions();
  const openSheet = useOpenSheet();
  const unavailable = !track.streamUrl;

  const play = () => {
    if (unavailable) return;
    if (isCurrent) return player.toggle();
    if (list) player.playTracks(list, index, context ?? null);
    else player.playTrack(track, context ?? null);
  };

  const openMenu = () =>
    openSheet((close) => (
      <Sheet open onClose={close} title={track.title}>
        <ActionList
          actions={[
            { label: track.liked ? 'Verwijderen uit Gelikete nummers' : 'Toevoegen aan Gelikete nummers', icon: <Icon name="heart" size={20} filled={track.liked} />, onClick: () => (close(), actions.toggleLike(track)) },
            { label: 'Toevoegen aan playlist', icon: <Icon name="playlistAdd" size={20} />, onClick: () => (close(), actions.addToPlaylist(track)) },
            { label: 'Hierna afspelen', icon: <Icon name="next" size={20} />, onClick: () => (close(), actions.queue(track, 'next')), hidden: unavailable },
            { label: 'Aan wachtrij toevoegen', icon: <Icon name="queue" size={20} />, onClick: () => (close(), actions.queue(track, 'end')), hidden: unavailable },
            { label: "Video's met dit geluid", icon: <Icon name="grid" size={20} />, onClick: () => (close(), navigate(`/sound/${track.id}`)) },
            { label: 'Naar artiest', icon: <Icon name="user" size={20} />, onClick: () => (close(), navigate(`/artist/${track.artist.id}`)) },
            { label: 'Naar album', icon: <Icon name="disc" size={20} />, onClick: () => (close(), navigate(`/album/${track.album!.id}`)), hidden: !track.album },
            { label: 'Delen', icon: <Icon name="share" size={20} />, onClick: () => (close(), actions.share(track)) },
            ...extraActions.map((a) => ({ ...a, onClick: () => (close(), a.onClick()) })),
          ]}
        />
      </Sheet>
    ));

  return (
    <div className={cx(styles.trackRow, isCurrent && styles.trackCurrent, unavailable && styles.trackUnavailable)}>
      <button className={styles.trackMain} onClick={play} aria-label={`${isCurrent && playing ? 'Pauzeer' : 'Speel'} ${track.title} van ${track.artist.name}`}>
        {number !== undefined && <span className={styles.trackNumber}>{isCurrent && playing ? <Equalizer /> : number}</span>}
        {showCover && (
          <span className={styles.trackCoverWrap}>
            <Cover src={track.coverUrl} size={48} />
            {isCurrent && number === undefined && <span className={styles.coverOverlay}>{playing ? <Equalizer /> : <Icon name="play" size={18} />}</span>}
          </span>
        )}
        <span className={styles.rowText}>
          <span className={styles.rowTitle}>{track.title}</span>
          <span className={styles.rowSub}>
            {!track.fullPlayback && track.streamUrl && <span className={styles.previewTag}>Preview</span>}
            {unavailable ? 'Niet beschikbaar' : track.artist.name}
            <span aria-hidden> · {formatDuration(track.clip?.durationSec ?? track.durationSec)}</span>
          </span>
        </span>
      </button>
      {trailing}
      <IconButton icon="heart" label={track.liked ? 'Niet meer leuk' : 'Vind ik leuk'} active={track.liked} filled={track.liked} size={20} onClick={() => actions.toggleLike(track)} />
      <IconButton icon="more" label="Meer opties" size={20} onClick={openMenu} />
    </div>
  );
}

export function Equalizer() {
  return (
    <span className={styles.eq} aria-hidden>
      <i />
      <i />
      <i />
    </span>
  );
}
